import React, { useState, useEffect } from 'react';
import { Modal, Button, List, Space as AntSpace, Input, Spin, Typography, message, Badge } from 'antd';
import { DeleteOutlined, SearchOutlined, UserOutlined, TeamOutlined, UserAddOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { SpaceUserGroup, SpaceUserGroupMember } from '../../../../../../../types/spaceUserGroups';
import { SpaceUserGroupService } from '../../../../../../../services/SpaceUserGroupService';
import { AkukoAPIService } from '../../../../../../../services/serviceClass';
import { IDENTITY_API } from '../../../../../../../configs/env';
import { Dictionary } from '@onaio/utils';
import './style.css';

const { Text } = Typography;
const { confirm } = Modal;

interface SpaceUser {
  uuid: string;
  email: string;
  name: string;
  image?: string;
}

interface ManageGroupMembersModalProps {
  visible: boolean;
  onCancel: () => void;
  loading: boolean;
  group: SpaceUserGroup;
  spaceId: string;
  onMemberRemoved: (memberId: string) => Promise<void>;
  onMembersAssigned: (memberIds: string[]) => Promise<void>;
}

const ManageGroupMembersModal: React.FC<ManageGroupMembersModalProps> = ({
  visible,
  onCancel,
  loading,
  group,
  spaceId,
  onMemberRemoved,
  onMembersAssigned,
}) => {
  const [searchText, setSearchText] = useState('');
  const [availableUsers, setAvailableUsers] = useState<SpaceUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  useEffect(() => {
    if (visible) {
      fetchAvailableUsers();
    } else {
      setSearchText('');
      setSelectedUsers([]);
    }
  }, [visible, spaceId]);

  const fetchAvailableUsers = async () => {
    try {
      setLoadingUsers(true);
      const getSpaceService = new AkukoAPIService(IDENTITY_API, 'space');
      const space = await getSpaceService.read(spaceId) as Dictionary;
      const getUsersService = new AkukoAPIService(IDENTITY_API, `user/space/${space.account_id}/${spaceId}`);
      const response = await getUsersService.list();
      
      const users = (response as any[]).map(user => ({
        uuid: user?.uuid,
        email: user?.email,
        name: user?.name || user?.email?.split('@')[0],
        image: user?.image
      }));
      setAvailableUsers(users);
    } catch (error) {
      message.error('Failed to load available users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const getFilteredMembers = () => {
    // Always return all members, no filtering
    return group.members || [];
  };

  const getFilteredAvailableUsers = () => {
    // First filter out current members
    const currentMemberIds = new Set(group.members?.map(member => member.id).filter(Boolean) || []);
    let filteredUsers = availableUsers.filter(user => user.uuid && !currentMemberIds.has(user.uuid));

    // Then apply search filter if there's search text
    if (searchText.trim()) {
      const searchTerm = searchText.toLowerCase();
      filteredUsers = filteredUsers.filter(user =>
        user.email.toLowerCase().includes(searchTerm) ||
        (user.name && user.name.toLowerCase().includes(searchTerm))
      );
    }

    return filteredUsers;
  };

  const handleUserSelect = (userId: string) => {
    if (!userId) {
      return;
    }
    
    setSelectedUsers(prev => {
      const cleanPrev = prev.filter(Boolean);
      const newSelected = cleanPrev.includes(userId) 
        ? cleanPrev.filter(id => id !== userId)
        : [...cleanPrev, userId];
      return newSelected;
    });
  };

  const handleAssignMembers = async () => {
    const validSelectedUsers = selectedUsers.filter(Boolean);
    
    if (validSelectedUsers.length === 0) {
      message.warning('Please select users to add');
      return;
    }

    try {
      await onMembersAssigned(validSelectedUsers);
      setSelectedUsers([]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to assign members:', error);
    }
  };

  const handleMemberRemove = (member: SpaceUserGroupMember) => {
    confirm({
      title: 'Remove Member',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to remove ${member.email} from this group?`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      centered: true,
      maskClosable: true,
      onOk: () => onMemberRemoved(member.id),
    });
  };

  const currentMembers = getFilteredMembers();
  const availableUsersList = getFilteredAvailableUsers();

  const renderAvatar = (image?: string, name?: string, email?: string) => {
    if (image && image.trim() !== '') {
      return (
        <img 
          src={`https://assets.akuko.io/${image}`}
          alt={name || email}
          style={{ width: 32, height: 32, borderRadius: '50%' }}
        />
      );
    }
    
    // Get first letter from name or email
    const displayName = name || email || '';
    const firstLetter = displayName.charAt(0).toUpperCase();
    
    return (
      <div style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: '#5C6BC0', // Indigo color
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '14px',
        fontWeight: 500
      }}>
        {firstLetter}
      </div>
    );
  };

  return (
    <Modal
      title={
        <div>
          <div>Manage Group Members</div>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            Group: {group.name}
          </Text>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="assign"
          type="primary"
          icon={<UserAddOutlined />}
          onClick={() => {
            handleAssignMembers();
          }}
          disabled={selectedUsers.length === 0}
          loading={loading}
        >
          Add Selected Users ({selectedUsers.length})
        </Button>
      ]}
    >
      <div className="manage-members-modal">
        <Input
          placeholder="Search available users by email"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{ marginBottom: 16 }}
          allowClear
          size="large"
        />

        <div className="members-container">
          <div className="current-members">
            <div className="section-header">
              <TeamOutlined />
              <Text strong>Current Members</Text>
              <Badge count={currentMembers.length} style={{ backgroundColor: '#52c41a' }} />
            </div>
            <Spin spinning={loading}>
              <List
                dataSource={currentMembers}
                renderItem={(member: SpaceUserGroupMember) => (
                  <List.Item
                    key={member.id}
                    actions={[
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleMemberRemove(member)}
                        disabled={loading}
                        className="delete-button"
                      />
                    ]}
                  >
                    <List.Item.Meta
                      avatar={renderAvatar(member.image, member.name, member.email)}
                      title={member.name || member.email.split('@')[0]}
                      description={member.email}
                    />
                  </List.Item>
                )}
                locale={{ emptyText: 'No members found' }}
              />
            </Spin>
          </div>

          <div className="available-users">
            <div className="section-header">
              <UserAddOutlined />
              <Text strong>Available Users ({selectedUsers.length} selected)</Text>
              <Badge count={availableUsersList.length} style={{ backgroundColor: '#1890ff' }} />
            </div>
            <Spin spinning={loadingUsers}>
              <List
                dataSource={availableUsersList}
                renderItem={user => {
                  const isSelected = selectedUsers.includes(user.uuid);
                  return (
                    <List.Item
                      key={user.uuid}
                      onClick={() => handleUserSelect(user.uuid)}
                      className={isSelected ? 'selected' : ''}
                    >
                      <List.Item.Meta
                        avatar={renderAvatar(user.image, user.name, user.email)}
                        title={user.name || user.email.split('@')[0]}
                        description={user.email}
                      />
                      {isSelected && (
                        <Badge status="processing" text="Selected" style={{ marginLeft: 8 }} />
                      )}
                    </List.Item>
                  );
                }}
                locale={{ emptyText: 'No users available' }}
              />
            </Spin>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ManageGroupMembersModal; 